/** @module Apparel/ProductSize/SizeSet */
define([
	"dojo/_base/declare",
	"underscore",
	"csi",
	"csi/locale",
	"csi/store/Names",
	"csi/store/Nodes",
	"csi/xml",
], function(declare, _, csi, locale, Names, Nodes, xml) {

	const SORT_ATTR = "SizeCode";

	function _get2dSizeCodes(size, cache) {
		const s1 = cache.get(size.Dimension1Size);
		const s2 = cache.get(size.Dimension2Size);
		return [s1[SORT_ATTR], s2[SORT_ATTR]];
	}

	function _key(size1, size2) {
		return size1.$URL + "^" + size2.$URL;
	}

	function _sortSizes(sizes, cache) {
		if (sizes[0].IsTwoDim) {
			sizes.sort((size1, size2) => {
				const codes1 = _get2dSizeCodes(size1, cache);
				const codes2 = _get2dSizeCodes(size2, cache);

				if (codes1[0] < codes2[0]) {
					return -1;
				} else if (codes1[0] > codes2[0]) {
					return 1;
				}

				return (codes1[1] < codes2[1]) ? -1 : 1;
			});
		} else {
			sizes.sort((size1, size2) => {
				return size1[SORT_ATTR] < size2[SORT_ATTR] ? -1 : 1;
			});
		}
	}

	var _SizeSet = declare(null, {
		/** @property {Cache} cache - cache containing size nodes */
		cache: null,

		/** @property {string} dim1Name - name of dimension 1 */
		dim1Name: null,

		/** @property {string} dim2Name - name of dimension 2 (if any) */
		dim2Name: null,

		/** @property {cnl[]} dim1SizeUrls - ordered list of sizes for 1st dimension */
		dim1SizeUrls: null,

		/** @property {cnl[]} dim2SizeUrls - ordered list of sizes for 2nd dimension - if sizes are only 1d, then this will be empty */
		dim2SizeUrls: null,

		/** @property {Set<cnl>} dim1SizeUrlSet - set of dimension 1 sizes */
		dim1SizeUrlSet: null,

		/** @property {Set<cnl>} dim2SizeUrlSet - set of dimension 2 sizes */
		dim2SizeUrlSet: null,

		/** @property {cnl[]} sizeUrls - all size URLs in the size set */
		sizeUrls: null,

		/** @property {Object<string,string>} _map1dSizesTo2dSize - mapping from 1d size URLs to their corresponding 2d size URL */
		_map1dSizesTo2dSize: null,

		/** @property {Object<string,number>} _mapIndices - mapping from size URL to index used to maintain original order */
		_mapIndices: null,

		/**
		 * Create SizeSet from given array of sizes (either 1d or 2d)
		 * @param {Cache} cache
		 * @param {string[]} sizeUrls
		 * @param {boolean} [bSort] - set to true if sizeUrls are not necessarily in the proper order
		 * @return {SizeSet}
		 */
		constructor: function(cache, sizeUrls, bSort) {
			let sizes = cache.getNodes(sizeUrls);
			const firstSize = sizes[0];
			const is2d = firstSize && firstSize.IsTwoDim;
			let size1s = sizes;
			let size2s;

			if (bSort && firstSize) {
				_sortSizes(sizes, cache);
				sizeUrls = sizes.map(xml.nodeGetURL);
			}

			// separate 2d sizes into components
			if (is2d) {
				var map = this._map1dSizesTo2dSize = {};
				var size1;
				var size2;

				size1s = [];
				size2s = [];

				_.each(sizes, function(size) {
					size1 = cache.get(size.Dimension1Size);
					size2 = cache.get(size.Dimension2Size);

					size1s.push(size1);
					size2s.push(size2);

					map[_key(size1, size2)] = size.$URL;
				});

				this.dim1Name = locale.getEnumName(size1.DimensionType);
				this.dim2Name = locale.getEnumName(size2.DimensionType);

				size1s = _.uniq(size1s);
				size2s = _.uniq(size2s);
			}

			// sort sizes - in some cases they may already be sorted, but in others that is not the case (OnlyForSizes)
			size1s = csi.sort(size1s, SORT_ATTR);
			size2s = csi.sort(size2s, SORT_ATTR);

			const urlAttr = "$URL";
			this.cache = cache;
			this.sizeUrls = sizeUrls;
			this.dim1SizeUrls = _.pluck(size1s, urlAttr);
			this.dim2SizeUrls = _.pluck(size2s, urlAttr);
			this._mapIndices = csi.hashArray(sizes, urlAttr, Number);

			// FIX: these sets could eventually replace the arrays above, but are just added now to support quicker lookups
			this.dim1SizeUrlSet = new Set(this.dim1SizeUrls);
			this.dim2SizeUrlSet = new Set(this.dim2SizeUrls);
		},

		/**
		 * Return 2d size URLs for given 1st dimension size URL
		 * @param {string} size1Url
		 * @return {string[]}
		 */
		get2dSizeUrlsFor1stDimension: function(size1Url) {
			return _.map(this.dim2SizeUrls, function(size2Url) {
				return this.get2dSizeUrl(size1Url, size2Url);
			}, this);
		},

		/**
		 * Return 2d size URL given 1d size URLs 
		 * @param {string} size1Url
		 * @param {string} size2Url
		 * @return {string|null} returns null if no 2d size for combination of 1d sizes
		 */
		get2dSizeUrl: function(size1Url, size2Url) {
			var map = this._map1dSizesTo2dSize;
			var size1 = this.cache.get(size1Url);
			var size2 = this.cache.get(size2Url);
			return (map && map[_key(size1, size2)]) || null;
		},

		/**
		 * Return all sizes in size set
		 * @return {C8Node[]}
		 */
		getAllSizes() {
			return this.cache.getNodes(this.sizeUrls);
		},

		/**
		 * Return data store for sizes in set; if sizes are 2d, then store contains only 1st dimension sizes
		 * @return {Nodes}
		 */
		getSize1Store: function() {
			const {cache, dim1SizeUrls} = this;

			return new Nodes({
				cachingStore: cache,
				data: cache.getNodes(dim1SizeUrls)
			});
		},

		/**
		 * Return nodes for sizes in set; if sizes are 2d, then store contains only 1st dimension sizes
		 * @return {Names}
		 */
		getSize1Names: function() {
			return Names.createFromNodes(this.cache.getNodes(this.dim1SizeUrls), {bSort: false});
		},

		/**
		 * Return data store for 2nd dimension sizes when sizes are 2d; otherwise null
		 * @return {Nodes}
		 */
		getSize2Store: function() {
			if (this.has2dSizes()) {
				return new Nodes({
					cachingStore: this.cache,
					data: this.cache.getNodes(this.dim2SizeUrls)
				});
			}

			return null;
		},

		/**
		 * Return nodes for 2nd dimension sizes when sizes are 2d; otherwise null
		 * @return {Names|null}
		 */
		getSize2Names: function() {
			return this.has2dSizes() ? Names.createFromNodes(this.cache.getNodes(this.dim2SizeUrls), {bSort: false}) : null;
		},

		/**
		 * Return name of size with the given url
		 * @param {string} url 
		 * @return {string}
		 */
		getSizeName: function(url) {
			const {cache} = this;
			return locale.getNodeStoredName(cache.get(url), cache);
		},

		/**
		 * Return subset of this SizeSet given arrays of 1d size URLs; an empty array means All (rather than None)
		 * @param {string[]} onlySize1Urls
		 * @param {string[]} [onlySize2Urls] (if 2d)
		 * @return {SizeSet}
		 */
		getSubSizeSet: function(onlySize1Urls, onlySize2Urls) {
			var bEmpty1 = !onlySize1Urls || !onlySize1Urls.length;
			var bEmpty2 = !onlySize2Urls || !onlySize2Urls.length;
			if (bEmpty1 && bEmpty2) {
				return this;
			}

			var sizeUrls;

			if (bEmpty1) {
				onlySize1Urls = this.dim1SizeUrls;
			}

			if (this.has2dSizes()) {
				if (bEmpty2) {
					onlySize2Urls = this.dim2SizeUrls;
				}

				sizeUrls = [];

				_.each(onlySize1Urls, function(size1Url) {
					_.each(onlySize2Urls, function(size2Url) {
						var sizeUrl = this.get2dSizeUrl(size1Url, size2Url);
						if (sizeUrl) {
							sizeUrls.push(sizeUrl);
						}
					}, this);
				}, this);
			} else {
				sizeUrls = onlySize1Urls;
			}

			// make sure order is maintained
			var mapIndices = this._mapIndices;
			sizeUrls = csi.sort(sizeUrls, function(url) {return mapIndices[url];});

			return new _SizeSet(this.cache, sizeUrls);
		},

		/**
		 * Return true if url is a first dimension size 
		 * @param {cnl} url 
		 * @return {boolean}
		 */
		has1dSize(url) {
			return !!this.dim1SizeUrlSet?.has(url);
		},

		/**
		 * Return true if url is a second dimension size 
		 * @param {cnl} url 
		 * @return {boolean}
		 */
		has2dSize(url) {
			return !!this.dim2SizeUrlSet?.has(url);
		},

		/**
		 * Return true if there are 2d sizes
		 * @return {boolean}
		 */
		has2dSizes: function() {
			return !!this._map1dSizesTo2dSize;
		}
	});

	return _SizeSet;
});
